import React, { useCallback, useEffect, useState } from 'react';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import { Badge, Button, Card, Col, Dropdown, Form, InputGroup, Row, Spinner, Table } from 'react-bootstrap';
import Swal from 'sweetalert2';
import axiosInstance from '../services/axiosInterceptor';
import useStudyYearSelector from './useStudyYearSelector';

const LEAVE_TYPES = [
  { value: 'sick',     label: 'ลาป่วย', bg: 'warning' },
  { value: 'personal', label: 'ลากิจ',  bg: 'info' },
];
const typeInfo = (t) => LEAVE_TYPES.find(x => x.value === t) || { label: t, bg: 'secondary' };
const todayStr = () => new Date().toISOString().split('T')[0];

export default function DisciplineStudentLeave() {
  const setSkin = useState(localStorage.getItem('skin-mode') ? 'dark' : '')[1];
  const { yearList, yearId, yearLabel, handleSelectYear } = useStudyYearSelector();

  const [keyword,   setKeyword]   = useState('');
  const [students,  setStudents]  = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched,  setSearched]  = useState(false);

  const [leaves, setLeaves] = useState([]);

  // ฟอร์มแจ้งลา
  const [selectedIds, setSelectedIds] = useState([]);   // study_id ที่เลือก
  const [leaveStart,  setLeaveStart]  = useState(todayStr());
  const [leaveEnd,    setLeaveEnd]    = useState(todayStr());
  const [leaveType,   setLeaveType]   = useState('sick');
  const [leaveNote,   setLeaveNote]   = useState('');
  const [saving,      setSaving]      = useState(false);

  const [message, setMessage] = useState('');
  const [error,   setError]   = useState('');

  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(''), 4000);
    return () => clearTimeout(t);
  }, [message]);

  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(''), 6000);
    return () => clearTimeout(t);
  }, [error]);

  // ─── โหลดรายการลาทั้งหมดของปีการศึกษา ────────────────────────────────────────
  const loadLeaves = useCallback(() => {
    if (!yearId) return;
    axiosInstance.get(`/api/discipline/leaves?yearId=${yearId}`)
      .then(res => setLeaves(res.data || []))
      .catch(() => setLeaves([]));
  }, [yearId]);

  useEffect(() => {
    setStudents([]);
    setSelectedIds([]);
    setSearched(false);
    loadLeaves();
  }, [yearId, loadLeaves]);

  // ─── ค้นหานักเรียน (เลขประจำตัว หรือ ชื่อ) ────────────────────────────────────
  const search = useCallback((e) => {
    e?.preventDefault();
    if (!yearId) return;
    setSearching(true);
    setError('');
    axiosInstance
      .get(`/api/discipline/conduct/students?yearId=${yearId}&keyword=${encodeURIComponent(keyword.trim())}`)
      .then(res => setStudents(res.data || []))
      .catch(() => setError('ไม่สามารถค้นหานักเรียนได้'))
      .finally(() => { setSearching(false); setSearched(true); });
  }, [yearId, keyword]);

  // ─── ฟอร์ม ─────────────────────────────────────────────────────────────────
  const toggleStudent = (studyId) => {
    setSelectedIds(prev => prev.includes(studyId)
      ? prev.filter(id => id !== studyId)
      : [...prev, studyId]);
  };
  const toggleAll = () => {
    const ids = students.map(s => s.study_id);
    setSelectedIds(prev => ids.every(id => prev.includes(id)) ? [] : ids);
  };

  const handleStartChange = (val) => {
    setLeaveStart(val);
    if (val > leaveEnd) setLeaveEnd(val);
  };

  const resetForm = () => {
    setSelectedIds([]);
    setLeaveStart(todayStr());
    setLeaveEnd(todayStr());
    setLeaveType('sick');
    setLeaveNote('');
  };

  const handleSave = () => {
    if (selectedIds.length === 0) {
      Swal.fire({ icon: 'warning', title: 'ยังไม่ได้เลือกนักเรียน', text: 'กรุณาเลือกนักเรียนอย่างน้อย 1 คน' });
      return;
    }
    if (leaveStart > leaveEnd) {
      Swal.fire({ icon: 'warning', title: 'ช่วงวันที่ไม่ถูกต้อง', text: 'วันที่เริ่มต้องไม่เกินวันที่สิ้นสุด' });
      return;
    }
    setSaving(true);
    axiosInstance.post('/api/discipline/leaves', {
      studyIds:  selectedIds,
      yearId,
      leaveStart,
      leaveEnd,
      leaveType,
      leaveNote: leaveNote.trim() || null,
    })
      .then(() => {
        Swal.fire({ icon: 'success', title: 'บันทึกการลาสำเร็จ', timer: 1200, showConfirmButton: false });
        resetForm();
        loadLeaves();
      })
      .catch(err => {
        Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: err.response?.data?.message || 'ไม่สามารถบันทึกได้' });
      })
      .finally(() => setSaving(false));
  };

  const handleDelete = (leave) => {
    Swal.fire({
      title: 'ยืนยันการลบ',
      text: `ลบการลาของ ${leave.st_name}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'ลบ',
      cancelButtonText: 'ยกเลิก',
      confirmButtonColor: '#dc3545',
    }).then(result => {
      if (!result.isConfirmed) return;
      axiosInstance.delete(`/api/discipline/leaves/${leave.leave_id}`)
        .then(() => {
          Swal.fire({ icon: 'success', title: 'ลบสำเร็จ', timer: 1000, showConfirmButton: false });
          loadLeaves();
        })
        .catch(err => {
          Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: err.response?.data?.message || 'ไม่สามารถลบได้' });
        });
    });
  };

  const today = todayStr();
  const coversToday = (l) => l.leave_start <= today && today <= l.leave_end;
  const allSelected = students.length > 0 && students.every(s => selectedIds.includes(s.study_id));

  return (
    <>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">

        {/* page header */}
        <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item">ฝ่ายปกครอง</li>
              <li className="breadcrumb-item active">แจ้งลานักเรียน</li>
            </ol>
            <h4 className="main-title mb-0">แจ้งลานักเรียน (ป่วย/ลากิจ)</h4>
          </div>
          <Dropdown>
            <Dropdown.Toggle variant="outline-primary">
              {yearLabel || 'เลือกปีการศึกษา'}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {yearList.map(y => (
                <Dropdown.Item key={y.year_id} onClick={() => handleSelectYear(y.year_id)}>
                  ปีการศึกษา {y.year_year}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>

        <div className="alert alert-info d-flex align-items-center gap-2 mb-4" role="alert">
          <i className="ri-information-line fs-5"></i>
          <span>ฝ่ายปกครองสามารถแจ้งลาให้นักเรียนทุกคน โดยค้นหาจากเลขประจำตัวหรือชื่อ
            &nbsp;— รายการลาจะแสดงเป็นสถานะ "ลา" อัตโนมัติในหน้าเช็คชื่อรายคาบของวันที่อยู่ในช่วงลา
            (มีผลกับการเช็คชื่อ<strong>ครั้งถัดไป</strong>เท่านั้น)</span>
        </div>

        {/* alerts */}
        {message && (
          <div className="alert alert-success alert-dismissible d-flex align-items-center gap-2 mb-3" role="alert">
            ✓ {message}
            <button type="button" className="btn-close ms-auto" onClick={() => setMessage('')} />
          </div>
        )}
        {error && (
          <div className="alert alert-danger alert-dismissible d-flex align-items-center gap-2 mb-3" role="alert">
            ⚠ {error}
            <button type="button" className="btn-close ms-auto" onClick={() => setError('')} />
          </div>
        )}

        <Row className="g-3">
          {/* ── ฟอร์มแจ้งลา ── */}
          <Col xl="5">
            <Card className="card-one h-100">
              <Card.Header><Card.Title as="h6">เพิ่มการลา</Card.Title></Card.Header>
              <Card.Body className="p-3">
                <Row className="g-2 mb-3">
                  <Col xs="6">
                    <Form.Group>
                      <Form.Label className="fw-semibold mb-1">วันที่เริ่ม</Form.Label>
                      <Form.Control type="date" value={leaveStart} onChange={e => handleStartChange(e.target.value)} />
                    </Form.Group>
                  </Col>
                  <Col xs="6">
                    <Form.Group>
                      <Form.Label className="fw-semibold mb-1">วันที่สิ้นสุด</Form.Label>
                      <Form.Control type="date" value={leaveEnd} min={leaveStart} onChange={e => setLeaveEnd(e.target.value)} />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold mb-1">ประเภทการลา</Form.Label>
                  <div className="d-flex gap-3">
                    {LEAVE_TYPES.map(t => (
                      <Form.Check
                        key={t.value}
                        type="radio"
                        name="leaveType"
                        id={`leaveType-${t.value}`}
                        label={t.label}
                        checked={leaveType === t.value}
                        onChange={() => setLeaveType(t.value)}
                      />
                    ))}
                  </div>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold mb-1">หมายเหตุ</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="เหตุผลการลา (ถ้ามี)"
                    value={leaveNote}
                    onChange={e => setLeaveNote(e.target.value)}
                    maxLength={255}
                  />
                </Form.Group>

                <Form.Group>
                  <Form.Label className="fw-semibold mb-1">ค้นหานักเรียน</Form.Label>
                  <Form onSubmit={search}>
                    <InputGroup>
                      <Form.Control
                        value={keyword}
                        onChange={e => setKeyword(e.target.value)}
                        placeholder="เลขประจำตัว หรือชื่อนักเรียน"
                        style={{ fontSize: 16 }}
                      />
                      <Button type="submit" variant="primary" disabled={searching}>
                        {searching
                          ? <Spinner animation="border" size="sm" />
                          : <><i className="ri-search-line me-1"></i>ค้นหา</>}
                      </Button>
                    </InputGroup>
                  </Form>
                </Form.Group>

                <Form.Group className="mt-3">
                  <div className="d-flex align-items-center justify-content-between mb-1">
                    <Form.Label className="fw-semibold mb-0">เลือกนักเรียน ({selectedIds.length})</Form.Label>
                    {students.length > 0 && (
                      <Button variant="link" size="sm" className="p-0" onClick={toggleAll}>
                        {allSelected ? 'ยกเลิกทั้งหมด' : 'เลือกทั้งหมด'}
                      </Button>
                    )}
                  </div>
                  <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: 6 }} className="p-2">
                    {students.length > 0 ? students.map(s => (
                      <Form.Check
                        key={s.study_id}
                        type="checkbox"
                        id={`st-${s.study_id}`}
                        label={`${s.st_idnumber} — ${s.st_name} (ม.${s.c_id}/${s.r_no})`}
                        checked={selectedIds.includes(s.study_id)}
                        onChange={() => toggleStudent(s.study_id)}
                      />
                    )) : (
                      <div className="text-muted text-center py-2">
                        {searched ? 'ไม่พบนักเรียน' : 'ค้นหาเพื่อเลือกนักเรียน'}
                      </div>
                    )}
                  </div>
                </Form.Group>

                <div className="mt-3 d-flex justify-content-end">
                  <Button variant="success" onClick={handleSave} disabled={saving}>
                    {saving
                      ? <><Spinner animation="border" size="sm" className="me-1" />กำลังบันทึก...</>
                      : <><i className="ri-save-line me-1"></i>บันทึกการลา</>}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* ── ตารางรายการลา ── */}
          <Col xl="7">
            <Card className="card-one h-100">
              <Card.Header className="d-flex align-items-center justify-content-between">
                <Card.Title as="h6" className="mb-0">รายการลาล่าสุด</Card.Title>
                {leaves.length > 0 && <Badge bg="secondary" className="fw-normal">{leaves.length} รายการ</Badge>}
              </Card.Header>
              <Card.Body className="p-3">
                <Table responsive hover className="mb-0">
                  <thead>
                    <tr>
                      <th scope="col">ชื่อ-สกุล</th>
                      <th scope="col" className="text-center" style={{ width: 70 }}>ห้อง</th>
                      <th scope="col" className="text-center" style={{ width: 170 }}>ช่วงวันลา</th>
                      <th scope="col" className="text-center" style={{ width: 80 }}>ประเภท</th>
                      <th scope="col">หมายเหตุ</th>
                      <th scope="col" className="text-center" style={{ width: 50 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaves.length > 0 ? leaves.map(l => (
                      <tr key={l.leave_id} className={coversToday(l) ? 'table-info' : ''}>
                        <td>
                          {l.st_name}
                          <div className="fs-xs text-secondary">
                            {l.st_idnumber}
                            {' · '}
                            <span className={l.source === 'discipline' ? 'text-primary' : ''}>
                              {l.source === 'discipline' ? 'ฝ่ายปกครองแจ้ง' : 'ครูประจำชั้นแจ้ง'}
                            </span>
                          </div>
                        </td>
                        <td className="text-center">{l.tb_class_c_id}/{l.r_no}</td>
                        <td className="text-center">
                          {l.leave_start === l.leave_end
                            ? l.leave_start
                            : <>{l.leave_start}<br />ถึง {l.leave_end}</>}
                        </td>
                        <td className="text-center">
                          <Badge bg={typeInfo(l.leave_type).bg}>{typeInfo(l.leave_type).label}</Badge>
                        </td>
                        <td><small>{l.leave_note || '-'}</small></td>
                        <td className="text-center">
                          <Button variant="outline-danger" size="sm" onClick={() => handleDelete(l)} title="ลบ">
                            <i className="ri-delete-bin-line"></i>
                          </Button>
                        </td>
                      </tr>
                    )) : (
                      <tr><td colSpan={6} className="text-center text-muted py-3">ยังไม่มีรายการลา</td></tr>
                    )}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Footer />
      </div>
    </>
  );
}
